<template>
  <IngredientsBaseSection
    title="Ingredients without DVs"
    :items="sortedLines"
    section-key="lines"
    :is-dv-line="false"
    :just-added-index="store.justAddedLineIndex"
    list-class="lines-list"
    focus-selector="input[name='ingredient']"
    :empty-message="emptyMessage"
    clear-type="lines"
    :on-sort-focus-in="handleSortFocusIn"
    :on-sort-focus-out="handleSortFocusOut"
    @add="store.addLine"
    @clear-added-index="store.clearJustAddedLineIndex"
  >
    <template #title>
      <span>Ingredients without DVs</span>
    </template>
    <template #extra-content-top>
      <WarningsBox :compliance="store.lineWarnings" />
    </template>
    <template #extra-content-after-buttons>
      <p v-if="hasLines" class="help mt-1 mb-4 lines-hint is-hidden-desktop">
        {{ dvHint }}
      </p>
    </template>
    <template #extra-content-bottom>
      <div class="mobile-add-wrapper is-hidden-desktop" data-sticky-footer>
        <div class="mobile-add-container">
          <button type="button" class="button is-success is-fullwidth" @click="handleMobileAdd">
            <span class="icon"><i class="fas fa-plus"></i></span>
            <span>Add {{ hasLines ? "Another " : "" }}Ingredient</span>
          </button>
        </div>
      </div>
      <p v-if="hasLines" class="help is-hidden-touch">
        {{ dvHint }}
      </p>
    </template>
  </IngredientsBaseSection>
</template>

<script setup>
  import { computed } from "vue";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import IngredientsBaseSection from "./ingredients-base-section.vue";
  import WarningsBox from "./warnings-box.vue";
  import { useFocusAwareSort } from "./composables/use-focus-aware-sort";
  import { sortByAmountDesc } from "./utils/sorting";
  import { focusAfterAdd } from "./utils/focus-helpers";

  const store = useSupplementLabelStore();

  // Focus-aware sorting: pins the focused item in place until user leaves the card
  const {
    sortedItems: sortedLines,
    handleFocusIn: handleSortFocusIn,
    handleFocusOut: handleSortFocusOut,
  } = useFocusAwareSort({
    items: () => store.linesWithoutDv,
    sortFn: (items) => sortByAmountDesc(items, "amount", "unit"),
  });

  const dvHint = "Ingredients without established Daily Values will show † on the label.";
  const baseEmptyMessage =
    "No ingredients added. Use this for proprietary ingredients or nutrients without established Daily Values.";

  const hasLines = computed(() => store.linesWithoutDv.length > 0);
  const emptyMessage = computed(() =>
    hasLines.value ? baseEmptyMessage : `${baseEmptyMessage} ${dvHint}`
  );

  function handleMobileAdd() {
    store.addLine();
    focusAfterAdd(".lines-list input[name='ingredient']", () => store.clearJustAddedLineIndex());
  }
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";

  .lines-hint {
    text-align: center;
  }

  .mobile-add-wrapper {
    @include sticky-mobile-footer(5);
  }

  .mobile-add-container {
    padding-top: 0;
    background: $white;
  }
</style>
